import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';


const ContentManagement = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  
  // Services
  const [services, setServices] = useState([]);
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  
  // Hero Slides
  const [heroSlides, setHeroSlides] = useState([]);
  const [showHeroDialog, setShowHeroDialog] = useState(false);
  const [editingHero, setEditingHero] = useState(null);
  
  // Events
  const [events, setEvents] = useState([]);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  
  // News
  const [news, setNews] = useState([]);
  const [showNewsDialog, setShowNewsDialog] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  
  // Form templates
  const [formTemplates, setFormTemplates] = useState([]);
  
  // Service form state
  const [serviceData, setServiceData] = useState({
    title: '',
    description: '',
    image_url: '',
    image_data: '',
    linked_form_id: '',
    order: 0
  });
  
  // Hero form state
  const [heroData, setHeroData] = useState({
    title: '',
    subtitle: '',
    cta_text: '',
    cta_link: '',
    image_url: '',
    image_data: '',
    order: 0
  });
  
  // Event form state
  const [eventData, setEventData] = useState({
    title: '',
    description: '',
    date: '',
    location: '',
    image_url: ''
  });
  
  // News form state
  const [newsData, setNewsData] = useState({
    title: '',
    content: '',
    category: '',
    author: ''
  });

  const [podcasts, setPodcasts] = useState([]);
  const [showPodcastDialog, setShowPodcastDialog] = useState(false);
  const [editingPodcast, setEditingPodcast] = useState(null);
  const [podcastData, setPodcastData] = useState({
    title: '',
    description: '',
    audio_url: '',
    episode_number: '',
    duration: ''
  });

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    } else if (user) {
      if (user.role !== 'admin') {
        toast.error('Access denied. Admin privileges required.');
        navigate('/');
        return;
      }
      fetchData();
    }
  }, [user, loading, navigate]);

  const fetchData = async () => {
    try {
      const [servicesRes, eventsRes, formsRes, heroRes, newsRes, podcastsRes] = await Promise.all([
        api.get(`/services`),
        api.get(`/events`),
        api.get(`/form-templates`),
        api.get(`/hero-slides`),
        api.get(`/news`),
        api.get(`/podcasts`)
      ]);
      setServices(servicesRes.data);
      setEvents(eventsRes.data);
      setFormTemplates(formsRes.data);
      setHeroSlides(heroRes.data);
      setNews(newsRes.data);
      setPodcasts(podcastsRes.data);
    } catch (error) {
      console.error('Failed to fetch data', error);
      toast.error('Failed to load data');
    }
  };

  // Service functions
  const handleServiceImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setServiceData({ ...serviceData, image_data: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const saveService = async () => {
    try {
      const service = {
        id: editingService?.id || uuidv4(),
        ...serviceData,
        active: true,
        created_at: editingService?.created_at || new Date().toISOString()
      };

      if (editingService) {
        await api.put(`/services/${service.id}`, service);
        toast.success('Service updated successfully');
      } else {
        await api.post(`/services`, service);
        toast.success('Service created successfully');
      }

      setShowServiceDialog(false);
      setEditingService(null);
      setServiceData({ title: '', description: '', image_url: '', image_data: '', linked_form_id: '', order: 0 });
      fetchData();
    } catch (error) {
      console.error('Failed to save service', error);
      toast.error('Failed to save service');
    }
  };

  const deleteService = async (id) => {
    if (!confirm('Are you sure you want to delete this service?')) return;
    try {
      await api.delete(`/services/${id}`);
      toast.success('Service deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to delete service', error);
      toast.error('Failed to delete service');
    }
  };


  // Hero functions
  const handleHeroImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setHeroData({ ...heroData, image_data: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const saveHeroSlide = async () => {
    try {
      const slide = {
        id: editingHero?.id || uuidv4(),
        ...heroData,
        active: true,
        created_at: editingHero?.created_at || new Date().toISOString()
      };

      if (editingHero) {
        await api.put(`/hero-slides/${slide.id}`, slide);
        toast.success('Hero slide updated successfully');
      } else {
        await api.post(`/hero-slides`, slide);
        toast.success('Hero slide created successfully');
      }

      setShowHeroDialog(false);
      setEditingHero(null);
      setHeroData({ title: '', subtitle: '', cta_text: '', cta_link: '', image_url: '', image_data: '', order: 0 });
      fetchData();
    } catch (error) {
      console.error('Failed to save hero slide', error);
      toast.error('Failed to save hero slide');
    }
  };

  const deleteHeroSlide = async (id) => {
    if (!confirm('Are you sure you want to delete this hero slide?')) return;
    try {
      await api.delete(`/hero-slides/${id}`);
      toast.success('Hero slide deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to delete hero slide', error);
      toast.error('Failed to delete hero slide');
    }
  };

  // Event functions
  const saveEvent = async () => {
    try {
      const event = {
        id: editingEvent?.id || uuidv4(),
        ...eventData,
        date: new Date(eventData.date).toISOString(),
        active: true,
        created_at: editingEvent?.created_at || new Date().toISOString()
      };

      if (editingEvent) {
        await api.put(`/events/${event.id}`, event);
        toast.success('Event updated successfully');
      } else {
        await api.post(`/events`, event);
        toast.success('Event created successfully');
      }

      setShowEventDialog(false);
      setEditingEvent(null);
      setEventData({ title: '', description: '', date: '', location: '', image_url: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to save event', error);
      toast.error('Failed to save event');
    }
  };

  const deleteEvent = async (id) => {
    if (!confirm('Are you sure you want to delete this event?')) return;
    try {
      await api.delete(`/events/${id}`);
      toast.success('Event deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to delete event', error);
      toast.error('Failed to delete event');
    }
  };


  // News functions
  const saveNews = async () => {
    try {
      const article = {
        id: editingNews?.id || uuidv4(),
        ...newsData,
        published_at: editingNews?.published_at || new Date().toISOString()
      };

      if (editingNews) {
        await api.put(`/news/${article.id}`, article);
        toast.success('News article updated successfully');
      } else {
        await api.post(`/news`, article);
        toast.success('News article created successfully');
      }

      setShowNewsDialog(false);
      setEditingNews(null);
      setNewsData({ title: '', content: '', category: '', author: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to save news', error);
      toast.error('Failed to save news');
    }
  };

  const deleteNews = async (id) => {
    if (!confirm('Are you sure you want to delete this news article?')) return;
    try {
      await api.delete(`/news/${id}`);
      toast.success('News article deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to delete news', error);
      toast.error('Failed to delete news');
    }
  };

  const resetPodcastData = () =>
    setPodcastData({ title: '', description: '', audio_url: '', episode_number: '', duration: '' });

  const savePodcast = async () => {
    try {
      const episode = {
        id: editingPodcast?.id || uuidv4(),
        ...podcastData,
        episode_number: podcastData.episode_number ? Number(podcastData.episode_number) : null,
        active: true,
        published_at: editingPodcast?.published_at || new Date().toISOString(),
        created_at: editingPodcast?.created_at || new Date().toISOString()
      };

      if (editingPodcast) {
        await api.put(`/podcasts/${episode.id}`, episode);
        toast.success('Podcast episode updated successfully');
      } else {
        await api.post(`/podcasts`, episode);
        toast.success('Podcast episode created successfully');
      }

      setShowPodcastDialog(false);
      setEditingPodcast(null);
      resetPodcastData();
      fetchData();
    } catch (error) {
      console.error('Failed to save podcast', error);
      toast.error('Failed to save podcast');
    }
  };

  const deletePodcast = async (id) => {
    if (!confirm('Are you sure you want to delete this podcast episode?')) return;
    try {
      await api.delete(`/podcasts/${id}`);
      toast.success('Podcast episode deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to delete podcast', error);
      toast.error('Failed to delete podcast');
    }
  };

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <PageShell>
      <PageHeader title="Content Management" />

        <Tabs defaultValue="hero" className="space-y-6">
          <TabsList>
            <TabsTrigger value="hero">Hero Slides</TabsTrigger>
            <TabsTrigger value="services">Expertise Services</TabsTrigger>
            <TabsTrigger value="events">Events</TabsTrigger>
            <TabsTrigger value="news">News Articles</TabsTrigger>
            <TabsTrigger value="podcasts">Podcasts</TabsTrigger>
          </TabsList>

          {/* Hero Slides Tab */}
          <TabsContent value="hero">
            <div className="flex justify-between items-center mb-6">
              <p className="font-sans text-base text-muted-foreground">
                Manage hero carousel slides on the home page
              </p>
              <Button
                onClick={() => {
                  setEditingHero(null);
                  setHeroData({ title: '', subtitle: '', cta_text: '', cta_link: '', image_url: '', image_data: '', order: 0 });
                  setShowHeroDialog(true);
                }}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Plus size={20} className="mr-2" /> Add Hero Slide
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {heroSlides.map((slide) => (
                <Card key={slide.id}>
                  <CardHeader>
                    <CardTitle className="font-serif text-xl font-semibold text-primary">
                      {slide.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-sans text-sm text-muted-foreground mb-2">
                      {slide.subtitle}
                    </p>
                    <p className="font-sans text-sm text-accent mb-4">
                      CTA: {slide.cta_text} → {slide.cta_link}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          setEditingHero(slide);
                          setHeroData({
                            title: slide.title,
                            subtitle: slide.subtitle,
                            cta_text: slide.cta_text,
                            cta_link: slide.cta_link,
                            image_url: slide.image_url || '',
                            image_data: slide.image_data || '',
                            order: slide.order
                          });
                          setShowHeroDialog(true);
                        }}
                        size="sm"
                        variant="outline"
                      >
                        <Edit size={16} className="mr-1" /> Edit
                      </Button>
                      <Button
                        onClick={() => deleteHeroSlide(slide.id)}
                        size="sm"
                        variant="destructive"
                      >
                        <Trash2 size={16} className="mr-1" /> Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services">
            <div className="flex justify-between items-center mb-6">
              <p className="font-sans text-base text-muted-foreground">
                Manage services displayed on the Expertise page
              </p>
              <Button
                onClick={() => {
                  setEditingService(null);
                  setServiceData({ title: '', description: '', image_url: '', linked_form_id: '', order: 0 });
                  setShowServiceDialog(true);
                }}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Plus size={20} className="mr-2" /> Add Service
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {services.map((service) => (
                <Card key={service.id}>
                  <CardHeader>
                    <CardTitle className="font-serif text-xl font-semibold text-primary">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-sans text-sm text-muted-foreground mb-4">
                      {service.description}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          setEditingService(service);
                          setServiceData({
                            title: service.title,
                            description: service.description,
                            image_url: service.image_url || '',
                            linked_form_id: service.linked_form_id,
                            order: service.order
                          });
                          setShowServiceDialog(true);
                        }}
                        size="sm"
                        variant="outline"
                      >
                        <Edit size={16} className="mr-1" /> Edit
                      </Button>
                      <Button
                        onClick={() => deleteService(service.id)}
                        size="sm"
                        variant="destructive"
                      >
                        <Trash2 size={16} className="mr-1" /> Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            <div className="flex justify-between items-center mb-6">
              <p className="font-sans text-base text-muted-foreground">
                Manage events displayed on the Events page
              </p>
              <Button
                onClick={() => {
                  setEditingEvent(null);
                  setEventData({ title: '', description: '', date: '', location: '', image_url: '' });
                  setShowEventDialog(true);
                }}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Plus size={20} className="mr-2" /> Add Event
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {events.map((event) => (
                <Card key={event.id}>
                  <CardHeader>
                    <CardTitle className="font-serif text-xl font-semibold text-primary">
                      {event.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-sans text-sm text-muted-foreground mb-2">
                      {event.description}
                    </p>
                    <p className="font-sans text-sm text-accent mb-4">
                      {new Date(event.date).toLocaleString()}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          setEditingEvent(event);
                          setEventData({
                            title: event.title,
                            description: event.description || '',
                            date: new Date(event.date).toISOString().slice(0, 16),
                            location: event.location || '',
                            image_url: event.image_url || ''
                          });
                          setShowEventDialog(true);
                        }}
                        size="sm"
                        variant="outline"
                      >
                        <Edit size={16} className="mr-1" /> Edit
                      </Button>
                      <Button
                        onClick={() => deleteEvent(event.id)}
                        size="sm"
                        variant="destructive"
                      >
                        <Trash2 size={16} className="mr-1" /> Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* News Tab */}
          <TabsContent value="news">
            <div className="flex justify-between items-center mb-6">
              <p className="font-sans text-base text-muted-foreground">
                Manage news articles displayed on the home page
              </p>
              <Button
                onClick={() => {
                  setEditingNews(null);
                  setNewsData({ title: '', content: '', category: '', author: '' });
                  setShowNewsDialog(true);
                }}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Plus size={20} className="mr-2" /> Add News Article
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-6">
              {news.map((article) => (
                <Card key={article.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="font-serif text-xl font-semibold text-primary">
                          {article.title}
                        </CardTitle>
                        <p className="font-sans text-sm text-accent mt-1">
                          {article.category} • By {article.author}
                        </p>
                      </div>
                      <p className="font-sans text-sm text-muted-foreground">
                        {new Date(article.published_at).toLocaleDateString()}
                      </p>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="font-sans text-sm text-muted-foreground mb-4">
                      {article.content.substring(0, 200)}...
                    </p>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          setEditingNews(article);
                          setNewsData({
                            title: article.title,
                            content: article.content,
                            category: article.category,
                            author: article.author
                          });
                          setShowNewsDialog(true);
                        }}
                        size="sm"
                        variant="outline"
                      >
                        <Edit size={16} className="mr-1" /> Edit
                      </Button>
                      <Button
                        onClick={() => deleteNews(article.id)}
                        size="sm"
                        variant="destructive"
                      >
                        <Trash2 size={16} className="mr-1" /> Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Podcasts Tab */}
          <TabsContent value="podcasts">
            <div className="flex justify-between items-center mb-6">
              <p className="font-sans text-base text-muted-foreground">
                Manage podcast episodes featured on the home page
              </p>
              <Button
                onClick={() => {
                  setEditingPodcast(null);
                  resetPodcastData();
                  setShowPodcastDialog(true);
                }}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="add-podcast-btn"
              >
                <Plus size={20} className="mr-2" /> Add Episode
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-6">
              {podcasts.map((episode) => (
                <Card key={episode.id} data-testid={`podcast-admin-${episode.id}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <CardTitle className="font-serif text-xl font-semibold text-primary">
                        {episode.episode_number ? `Ep. ${episode.episode_number}: ` : ''}
                        {episode.title}
                      </CardTitle>
                      {episode.duration ? (
                        <p className="font-sans text-sm text-muted-foreground">{episode.duration}</p>
                      ) : null}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {episode.description ? (
                      <p className="font-sans text-sm text-muted-foreground mb-4">
                        {episode.description}
                      </p>
                    ) : null}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          setEditingPodcast(episode);
                          setPodcastData({
                            title: episode.title || '',
                            description: episode.description || '',
                            audio_url: episode.audio_url || '',
                            episode_number: episode.episode_number ?? '',
                            duration: episode.duration || ''
                          });
                          setShowPodcastDialog(true);
                        }}
                        size="sm"
                        variant="outline"
                      >
                        <Edit size={16} className="mr-1" /> Edit
                      </Button>
                      <Button
                        onClick={() => deletePodcast(episode.id)}
                        size="sm"
                        variant="destructive"
                        data-testid={`delete-podcast-${episode.id}`}
                      >
                        <Trash2 size={16} className="mr-1" /> Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

      {/* Service Dialog */}
      <Dialog open={showServiceDialog} onOpenChange={setShowServiceDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-bold text-primary">
              {editingService ? 'Edit Service' : 'Add Service'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Title</Label>
              <Input
                value={serviceData.title}
                onChange={(e) => setServiceData({ ...serviceData, title: e.target.value })}
                placeholder="Service title"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={serviceData.description}
                onChange={(e) => setServiceData({ ...serviceData, description: e.target.value })}
                placeholder="Service description"
                rows={4}
              />
            </div>
            <div>
              <Label>Image</Label>
              <div className="space-y-3">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleServiceImageUpload}
                  className="cursor-pointer"
                />
                <div className="text-center font-sans text-sm text-muted-foreground">OR</div>
                <Input
                  value={serviceData.image_url}
                  onChange={(e) => setServiceData({ ...serviceData, image_url: e.target.value })}
                  placeholder="Paste image URL"
                />
                {(serviceData.image_data || serviceData.image_url) && (
                  <div className="mt-2">
                    <img
                      src={serviceData.image_data || serviceData.image_url}
                      alt="Preview"
                      className="h-32 w-full object-cover border border-border"
                    />
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label>Linked Form</Label>
              <Select
                value={serviceData.linked_form_id}
                onValueChange={(value) => setServiceData({ ...serviceData, linked_form_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a form" />
                </SelectTrigger>
                <SelectContent>
                  {formTemplates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Display Order</Label>
              <Input
                type="number"
                value={serviceData.order}
                onChange={(e) => setServiceData({ ...serviceData, order: parseInt(e.target.value) })}
              />
            </div>
            <div className="flex gap-4">
              <Button
                onClick={() => setShowServiceDialog(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={saveService}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                disabled={!serviceData.title || !serviceData.linked_form_id}
              >
                Save Service
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Hero Dialog */}
      <Dialog open={showHeroDialog} onOpenChange={setShowHeroDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-bold text-primary">
              {editingHero ? 'Edit Hero Slide' : 'Add Hero Slide'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Title</Label>
              <Input
                value={heroData.title}
                onChange={(e) => setHeroData({ ...heroData, title: e.target.value })}
                placeholder="Main heading"
              />
            </div>
            <div>
              <Label>Subtitle</Label>
              <Input
                value={heroData.subtitle}
                onChange={(e) => setHeroData({ ...heroData, subtitle: e.target.value })}
                placeholder="Subheading text"
              />
            </div>
            <div>
              <Label>CTA Button Text</Label>
              <Input
                value={heroData.cta_text}
                onChange={(e) => setHeroData({ ...heroData, cta_text: e.target.value })}
                placeholder="Learn More"
              />
            </div>
            <div>
              <Label>CTA Link</Label>
              <Input
                value={heroData.cta_link}
                onChange={(e) => setHeroData({ ...heroData, cta_link: e.target.value })}
                placeholder="/about or /expertise"
              />
            </div>
            <div>
              <Label>Background Image</Label>
              <div className="space-y-3">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleHeroImageUpload}
                  className="cursor-pointer"
                />
                <div className="text-center font-sans text-sm text-muted-foreground">OR</div>
                <Input
                  value={heroData.image_url}
                  onChange={(e) => setHeroData({ ...heroData, image_url: e.target.value })}
                  placeholder="Paste image URL"
                />
                {(heroData.image_data || heroData.image_url) && (
                  <div className="mt-2">
                    <img
                      src={heroData.image_data || heroData.image_url}
                      alt="Preview"
                      className="h-32 w-full object-cover border border-border"
                    />
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label>Display Order</Label>
              <Input
                type="number"
                value={heroData.order}
                onChange={(e) => setHeroData({ ...heroData, order: parseInt(e.target.value) })}
              />
            </div>
            <div className="flex gap-4">
              <Button
                onClick={() => setShowHeroDialog(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={saveHeroSlide}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                disabled={!heroData.title || !heroData.subtitle}
              >
                Save Hero Slide
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-bold text-primary">
              {editingEvent ? 'Edit Event' : 'Add Event'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Title</Label>
              <Input
                value={eventData.title}
                onChange={(e) => setEventData({ ...eventData, title: e.target.value })}
                placeholder="Event title"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={eventData.description}
                onChange={(e) => setEventData({ ...eventData, description: e.target.value })}
                placeholder="Event description"
                rows={4}
              />
            </div>
            <div>
              <Label>Date & Time</Label>
              <Input
                type="datetime-local"
                value={eventData.date}
                onChange={(e) => setEventData({ ...eventData, date: e.target.value })}
              />
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={eventData.location}
                onChange={(e) => setEventData({ ...eventData, location: e.target.value })}
                placeholder="Event location"
              />
            </div>
            <div>
              <Label>Image URL</Label>
              <Input
                value={eventData.image_url}
                onChange={(e) => setEventData({ ...eventData, image_url: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
            </div>
            <div className="flex gap-4">
              <Button
                onClick={() => setShowEventDialog(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={saveEvent}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                disabled={!eventData.title || !eventData.date}
              >
                Save Event
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* News Dialog */}
      <Dialog open={showNewsDialog} onOpenChange={setShowNewsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-bold text-primary">
              {editingNews ? 'Edit News Article' : 'Add News Article'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Title</Label>
              <Input
                value={newsData.title}
                onChange={(e) => setNewsData({ ...newsData, title: e.target.value })}
                placeholder="Article title"
              />
            </div>
            <div>
              <Label>Content</Label>
              <Textarea
                value={newsData.content}
                onChange={(e) => setNewsData({ ...newsData, content: e.target.value })}
                placeholder="Article content"
                rows={8}
              />
            </div>
            <div>
              <Label>Category</Label>
              <Input
                value={newsData.category}
                onChange={(e) => setNewsData({ ...newsData, category: e.target.value })}
                placeholder="e.g., Legal Updates, Firm News, Community"
              />
            </div>
            <div>
              <Label>Author</Label>
              <Input
                value={newsData.author}
                onChange={(e) => setNewsData({ ...newsData, author: e.target.value })}
                placeholder="Author name"
              />
            </div>
            <div className="flex gap-4">
              <Button
                onClick={() => setShowNewsDialog(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={saveNews}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                disabled={!newsData.title || !newsData.content}
              >
                Save Article
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Podcast Dialog */}
      <Dialog open={showPodcastDialog} onOpenChange={setShowPodcastDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-bold text-primary">
              {editingPodcast ? 'Edit Podcast Episode' : 'Add Podcast Episode'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Title</Label>
              <Input
                value={podcastData.title}
                onChange={(e) => setPodcastData({ ...podcastData, title: e.target.value })}
                placeholder="Episode title"
                data-testid="podcast-title-input"
              />
            </div>
            <div>
              <Label>Audio URL</Label>
              <Input
                value={podcastData.audio_url}
                onChange={(e) => setPodcastData({ ...podcastData, audio_url: e.target.value })}
                placeholder="https://.../episode.mp3 or embed URL"
                data-testid="podcast-audio-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={podcastData.description}
                onChange={(e) => setPodcastData({ ...podcastData, description: e.target.value })}
                placeholder="Episode description"
                rows={5}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Episode number</Label>
                <Input
                  type="number"
                  value={podcastData.episode_number}
                  onChange={(e) => setPodcastData({ ...podcastData, episode_number: e.target.value })}
                  placeholder="1"
                />
              </div>
              <div>
                <Label>Duration</Label>
                <Input
                  value={podcastData.duration}
                  onChange={(e) => setPodcastData({ ...podcastData, duration: e.target.value })}
                  placeholder="e.g., 42:15"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <Button
                onClick={() => setShowPodcastDialog(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={savePodcast}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                disabled={!podcastData.title || !podcastData.audio_url}
                data-testid="save-podcast-btn"
              >
                Save Episode
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};

export default ContentManagement;
